import React from 'react'
import { motion } from 'framer-motion'
import { Award, Cloud, Users, Sparkles } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

const Team: React.FC = () => {
  const { t } = useLanguage()

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  }

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  }

  const teamMembers = [
    {
      name: t('team.christelle.name'),
      role: t('team.christelle.role'),
      qualification: t('team.christelle.qualification'),
      image: '/Chris.jpeg',
      accomplishments: [
        t('team.christelle.accomplishment1'),
        t('team.christelle.accomplishment2'),
        t('team.christelle.accomplishment3')
      ],
      description: t('team.christelle.description'),
      icon: Award,
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      name: t('team.saqib.name'),
      role: t('team.saqib.role'),
      qualification: t('team.saqib.qualification'),
      image: '/Saqib.jpeg',
      accomplishments: [
        t('team.saqib.accomplishment1'),
        t('team.saqib.accomplishment2'),
        t('team.saqib.accomplishment3'),
        t('team.saqib.accomplishment4')
      ],
      description: t('team.saqib.description'),
      icon: Cloud,
      gradient: 'from-blue-500 to-cyan-500'
    }
  ]

  return (
    <section id="team" className="py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50" role="region" aria-label="Team section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={containerVariants}
          className="text-center mb-16"
        >
          <motion.div variants={itemVariants} className="flex justify-center mb-4">
            <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full">
              <Users className="h-8 w-8 text-white" />
            </div>
          </motion.div>
          <motion.h2
            variants={itemVariants}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            {t('team.title')}
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
          >
            {t('team.description')}
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12"
        >
          {teamMembers.map((member, index) => {
            const IconComponent = member.icon
            return (
              <motion.div
                key={index}
                variants={cardVariants}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
                className="relative group"
              >
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 border border-gray-100">
                  {/* Gradient Header */}
                  <div className={`h-32 bg-gradient-to-r ${member.gradient} relative overflow-hidden flex items-center justify-between px-6`}>
                    <div className="absolute inset-0 bg-black opacity-10"></div>
                    {member.role === 'CTO' ? (
                      <div className="relative z-10 text-white">
                        <h3 className="text-2xl font-bold mb-1">
                          {member.name}
                        </h3>
                        <p className="text-lg font-semibold opacity-90">
                          {member.role}
                        </p>
                      </div>
                    ) : null}
                    <div className="absolute top-4 right-4">
                      <div className="p-2 bg-white/20 backdrop-blur-sm rounded-full">
                        <IconComponent className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Profile Section */}
                  <div className="px-8 pb-8 pt-4">
                    {/* Profile Image - Only show if not CTO */}
                    {member.role !== 'CTO' && (
                      <div className="flex justify-center -mt-16 mb-4">
                        <div className="relative">
                          <div className={`absolute inset-0 bg-gradient-to-r ${member.gradient} rounded-full blur-lg opacity-50 group-hover:opacity-75 transition-opacity`}></div>
                          <motion.img
                            src={member.image}
                            alt={member.name}
                            className="relative w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                            whileHover={{ scale: 1.05 }}
                            transition={{ duration: 0.3 }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Name and Role - Only show if not CTO (CTO name/role is in header) */}
                    {member.role !== 'CTO' && (
                      <div className="text-center mb-4">
                        <h3 className="text-2xl font-bold text-gray-900 mb-1">
                          {member.name}
                        </h3>
                        <p className="text-lg font-semibold text-gray-600 mb-2">
                          {member.role}
                        </p>
                        <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                          <Award className="h-4 w-4" />
                          <span>{member.qualification}</span>
                        </div>
                      </div>
                    )}
                    {/* Qualification - Show for CTO if not in header */}
                    {member.role === 'CTO' && (
                      <div className="text-center mb-4 pt-4">
                        <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                          <Award className="h-4 w-4" />
                          <span>{member.qualification}</span>
                        </div>
                      </div>
                    )}

                    {/* Description */}
                    <p className="text-gray-600 text-center mb-6 leading-relaxed">
                      {member.description}
                    </p>

                    {/* Accomplishments */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-purple-500" />
                        {t('team.keyAccomplishments')}
                      </h4>
                      <ul className="space-y-2">
                        {member.accomplishments.map((accomplishment, accIndex) => (
                          <li key={accIndex} className="flex items-start gap-3">
                            <div className={`mt-1.5 flex-shrink-0 w-2 h-2 rounded-full bg-gradient-to-r ${member.gradient}`}></div>
                            <span className="text-sm text-gray-600 leading-relaxed">
                              {accomplishment}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 shadow-xl">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              {t('team.cta.title')}
            </h3>
            <p className="text-blue-100 text-lg mb-6 max-w-2xl mx-auto">
              {t('team.cta.description')}
            </p>
            <motion.a
              href="#contact"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block px-8 py-3 bg-white text-blue-600 font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              {t('team.cta.button')}
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default Team

